@file:OptIn(ExperimentalUuidApi::class, ExperimentalCoroutinesApi::class, ExperimentalAtomicApi::class)

package ai.wagemix.data.firebase

import ai.wagemix.data.WMBaseSyncDataSource
import ai.wagemix.data.WMLocalContextDataSource
import ai.wagemix.data.WMMixSyncDataSource
import ai.wagemix.data.WMMixSyncEvent
import ai.wagemix.data.WMRemoteException
import ai.wagemix.data.dataOrThrow
import ai.wagemix.data.throwUnlessSuccess
import ai.wagemix.net.client.WagemixClientAPI
import ai.wagemix.net.models.WMMixFilters
import ai.wagemix.net.models.WMOwnedShare
import ai.wagemix.net.models.WMShareResult
import ai.wagemix.net.models.WMSharedMixCreate
import ai.wagemix.net.models.WMStoredMix
import ai.wagemix.net.models.WMUpload
import ai.wagemix.net.models.firebase.KxsWMSharedMix
import ai.wagemix.net.models.firebase.WMFirebaseMix
import ai.wagemix.net.models.firebase.WMSharedMix
import ai.wagemix.net.models.of
import dev.gitlive.firebase.auth.FirebaseAuth
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.catch
import ai.wagemix.net.models.firebase.WMSubscriptionType
import ai.wagemix.net.models.firebase.WMUserStatus
import ai.wagemix.net.models.firebase.entitledPlanOf
import ai.wagemix.net.models.kxs
import ai.wagemix.net.models.WMSharedMixActivation
import ai.wagemix.net.models.WMShareStats
import ai.wagemix.data.WMSharedLinkDeactivatedException
import ai.wagemix.data.WMPlanRequiredException
import com.fsryan.foundation.models.toEnumValue
import dev.gitlive.firebase.firestore.code
import dev.gitlive.firebase.firestore.FirestoreExceptionCode
import dev.gitlive.firebase.firestore.FirebaseFirestoreException
import dev.gitlive.firebase.firestore.CollectionReference
import dev.gitlive.firebase.firestore.DocumentReference
import dev.gitlive.firebase.firestore.Direction
import dev.gitlive.firebase.firestore.FieldValue
import dev.gitlive.firebase.firestore.FirebaseFirestore
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CoroutineStart
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.collect
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlin.concurrent.atomics.AtomicReference
import kotlin.concurrent.atomics.ExperimentalAtomicApi
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Firestore as the [WMMixSyncDataSource]: the stored-mix writes, the public shared-mix
 * observation, and, from construction, the mirror of the signed-in user's `storedMixes`,
 * `uploads` and `sharedMixes` collections into that user's local context. Share links are created through the
 * Wagemix API, which supplies the user's bearer token itself.
 */
fun WMFirebaseMixSyncDataSource(
    auth: FirebaseAuth,
    firestore: FirebaseFirestore,
    api: WagemixClientAPI,
    localContext: WMLocalContextDataSource
): WMMixSyncDataSource {
    return WMFirebaseMixSyncDataSourceImpl(firestoreApi = WMMixFirestoreApi(auth, firestore), api = api, localContext = localContext)
}

internal class WMFirebaseMixSyncDataSourceImpl(
    private val firestoreApi: WMMixFirestoreApi,
    private val api: WagemixClientAPI,
    localContext: WMLocalContextDataSource,
    scope: CoroutineScope = CoroutineScope(SupervisorJob())
): WMBaseSyncDataSource<WMMixSyncEvent<*>>(localContext, scope), WMMixSyncDataSource {

    /**
     * The signed-in user's collections, mirrored for the life of the data source while their plan is Basic or Pro. The
     * rules refuse them on any other plan (planning/paid-feature-enforcement-plan.md W3), so a free or lapsed account
     * mirrors empty collections, and a refused read (the plan just changed) is empty rather than the end of the mirror.
     * Nothing is deleted in Firestore: renewing mirrors them again.
     */
    @Suppress("unused")
    private val mirror: Job = firestoreApi.signedInUidFlow
        .distinctUntilChanged()
        .flatMapLatest { uid ->
            when (uid) {
                null -> emptyFlow()
                else -> firestoreApi.isPaidFlow(uid)
                    .distinctUntilChanged()
                    .flatMapLatest { paid -> if (paid) collectionsOf(uid) else flowOf(WMUserCollections.empty(uid)) }
            }
        }
        .onEach { collections ->
            localContext.upsertStoredMixes(collections.uid, collections.mixes)
            localContext.upsertUploads(collections.uid, collections.uploads)
            localContext.upsertOwnShares(collections.uid, collections.shares)
        }
        .launchIn(scope)

    /**
     * The click statistics of the user's share links, mirrored only while their plan is Pro: the rules refuse anyone
     * else, and a refused read (the plan changed) is no statistics rather than a failure.
     */
    @Suppress("unused")
    private val statsMirror: Job = firestoreApi.signedInUidFlow
        .distinctUntilChanged()
        .flatMapLatest { uid ->
            when (uid) {
                null -> emptyFlow()
                else -> firestoreApi.isProFlow(uid)
                    .distinctUntilChanged()
                    .flatMapLatest { pro ->
                        if (pro) {
                            firestoreApi.shareStatsFlow(uid)
                                .map<List<WMShareStats>, List<WMShareStats>?> { it }
                                .catch { emit(null) }
                        } else {
                            flowOf(null)
                        }
                    }
                    .map { stats -> uid to stats }
            }
        }
        .onEach { (uid, stats) -> localContext.upsertShareStats(uid, stats) }
        .launchIn(scope)

    private fun collectionsOf(uid: String): Flow<WMUserCollections> {
        return combine(
            firestoreApi.storedMixesFlow(uid),
            firestoreApi.uploadsFlow(uid),
            firestoreApi.ownSharesFlow(uid),
        ) { mixes, uploads, shares ->
            WMUserCollections(uid, mixes, uploads, shares)
        }.catch { emit(WMUserCollections.empty(uid)) }
    }

    /** Listener jobs by shared-mix id; observe and stop may run concurrently, so the map is swapped atomically. */
    private val sharedMixListeners = AtomicReference<Map<String, Job>>(emptyMap())

    override fun saveMix(
        uid: String,
        title: String,
        occupations: List<WMFirebaseMix.Occupation>,
        filters: WMMixFilters,
        additionalContext: Map<String, Any>
    ) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_SAVE,
                scope = uid,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.SaveMix.Initial(uid, title, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.SaveMix.Success(uid, title, sequenceId, additionalContext) },
                failure = { error -> WMMixSyncEvent.SaveMix.Failure(uid, title, error, sequenceId, additionalContext) },
                remote = { firestoreApi.saveMix(uid, title, occupations, filters) },
                local = { mixId -> localContext.upsertSelectedMixId(uid, mixId) }
            )
        }
    }

    override fun updateMix(
        uid: String,
        mixId: String,
        title: String,
        occupations: List<WMFirebaseMix.Occupation>,
        filters: WMMixFilters,
        additionalContext: Map<String, Any>
    ) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_UPDATE,
                scope = mixId,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.UpdateMix.Initial(uid, mixId, title, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.UpdateMix.Success(uid, mixId, title, sequenceId, additionalContext) },
                failure = { error -> WMMixSyncEvent.UpdateMix.Failure(uid, mixId, title, error, sequenceId, additionalContext) },
                remote = { firestoreApi.updateMix(uid, mixId, title, occupations, filters) },
                local = { }
            )
        }
    }

    override fun renameMix(uid: String, mixId: String, title: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_RENAME,
                scope = mixId,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.RenameMix.Initial(uid, mixId, title, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.RenameMix.Success(uid, mixId, title, sequenceId, additionalContext) },
                failure = { error -> WMMixSyncEvent.RenameMix.Failure(uid, mixId, title, error, sequenceId, additionalContext) },
                remote = { firestoreApi.renameMix(uid, mixId, title) },
                local = { }
            )
        }
    }

    override fun duplicateMix(uid: String, mixId: String, title: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_DUPLICATE,
                scope = mixId,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.DuplicateMix.Initial(uid, mixId, title, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.DuplicateMix.Success(uid, mixId, title, sequenceId, additionalContext) },
                failure = { error -> WMMixSyncEvent.DuplicateMix.Failure(uid, mixId, title, error, sequenceId, additionalContext) },
                remote = {
                    val source = firestoreApi.fetchMix(uid, mixId) ?: throw WMRemoteException(status = 404, message = "Mix not found")
                    firestoreApi.saveMix(uid, title, source.occupations, source.filters)
                },
                local = { newId -> localContext.upsertSelectedMixId(uid, newId) }
            )
        }
    }

    override fun deleteMix(uid: String, mixId: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_DELETE,
                scope = mixId,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.DeleteMix.Initial(uid, mixId, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.DeleteMix.Success(uid, mixId, sequenceId, additionalContext) },
                failure = { error -> WMMixSyncEvent.DeleteMix.Failure(uid, mixId, error, sequenceId, additionalContext) },
                remote = { firestoreApi.deleteMix(uid, mixId) },
                local = {
                    if (localContext.retrieveUser(uid).selectedMixId() == mixId) {
                        localContext.deleteSelectedMixId(uid)
                    }
                }
            )
        }
    }

    override fun createShare(uid: String, request: WMSharedMixCreate, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_SHARE,
                scope = request.mixId,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.CreateShare.Initial(uid, request.mixId, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.CreateShare.Success(uid, request.mixId, sequenceId, additionalContext) },
                failure = { error -> WMMixSyncEvent.CreateShare.Failure(uid, request.mixId, error, sequenceId, additionalContext) },
                remote = {
                    val response = api.createSharedMixUrl { body(request) }.dataOrThrow { it?.message ?: "No share link in the response" }
                    WMShareResult.of(
                        mixId = request.mixId,
                        sharedMixId = response.shareableUrl.substringAfterLast('/').substringBefore('?'),
                        shareableUrl = response.shareableUrl
                    )
                },
                local = { result -> localContext.upsertShareResult(uid, result) }
            )
        }
    }

    override fun setShareActive(uid: String, sharedMixId: String, active: Boolean, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_SET_SHARE_ACTIVE,
                scope = sharedMixId,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.SetShareActive.Initial(uid, sharedMixId, active, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.SetShareActive.Success(uid, sharedMixId, active, sequenceId, additionalContext) },
                failure = { error ->
                    WMMixSyncEvent.SetShareActive.Failure(uid, sharedMixId, active, error, sequenceId, additionalContext)
                },
                remote = {
                    api.setSharedMixActive {
                        sharedMixId(sharedMixId)
                        body(WMSharedMixActivation.kxs(active = active))
                    }.throwUnlessSuccess { it?.message ?: "The link could not be changed" }
                },
                // The mirror of the user's shares carries the new state.
                local = { }
            )
        }
    }

    override fun regenerateSharePreview(uid: String, sharedMixId: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        launch {
            guarded(
                operation = OP_REGENERATE_PREVIEW,
                scope = sharedMixId,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.RegenerateSharePreview.Initial(uid, sharedMixId, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.RegenerateSharePreview.Success(uid, sharedMixId, sequenceId, additionalContext) },
                failure = { error ->
                    WMMixSyncEvent.RegenerateSharePreview.Failure(uid, sharedMixId, error, sequenceId, additionalContext)
                },
                remote = {
                    api.regenerateSharedMixPreview { sharedMixId(sharedMixId) }
                        .throwUnlessSuccess { it?.message ?: "The preview could not be regenerated" }
                },
                // The mirror of the user's shares carries the pending status and, later, the image.
                local = { }
            )
        }
    }

    override fun observeSharedMix(sharedMixId: String, additionalContext: Map<String, Any>) {
        val sequenceId = Uuid.random()
        val listener = launch(start = CoroutineStart.LAZY) {
            guarded(
                operation = OP_OBSERVE_SHARED,
                scope = sharedMixId,
                sequenceId = sequenceId,
                initial = { WMMixSyncEvent.ObserveSharedMix.Initial(sharedMixId, sequenceId, additionalContext) },
                success = { WMMixSyncEvent.ObserveSharedMix.Success(sharedMixId, sequenceId, additionalContext) },
                failure = { error -> WMMixSyncEvent.ObserveSharedMix.Failure(sharedMixId, error, sequenceId, additionalContext) },
                remote = {
                    firestoreApi.sharedMixFlow(sharedMixId).first()
                        ?: throw WMRemoteException(status = 404, message = "Shared mix not found")
                },
                local = { mix -> localContext.upsertSharedMix(sharedMixId, mix) }
            )
            // The first snapshot is the sync; later snapshots (the rendered preview image) keep the mirror current. A
            // link turned off meanwhile ends the listener; the page keeps what it showed.
            firestoreApi.sharedMixFlow(sharedMixId)
                .onEach { mix -> if (mix != null) localContext.upsertSharedMix(sharedMixId, mix) }
                .catch { }
                .collect()
        }
        if (registerListener(sharedMixId, listener)) {
            listener.start()
        } else {
            listener.cancel()
        }
    }

    override fun stopObservingSharedMix(sharedMixId: String) {
        while (true) {
            val current = sharedMixListeners.load()
            val listener = current[sharedMixId] ?: return
            if (sharedMixListeners.compareAndSet(current, current - sharedMixId)) {
                listener.cancel()
                return
            }
        }
    }

    /** Puts [listener] under [sharedMixId] unless one is already observing it; `true` when this caller won. */
    private fun registerListener(sharedMixId: String, listener: Job): Boolean {
        while (true) {
            val current = sharedMixListeners.load()
            if (current.containsKey(sharedMixId)) {
                return false
            }
            if (sharedMixListeners.compareAndSet(current, current + (sharedMixId to listener))) {
                return true
            }
        }
    }

    private companion object {
        const val OP_SAVE = "mix.save"
        const val OP_UPDATE = "mix.update"
        const val OP_RENAME = "mix.rename"
        const val OP_DUPLICATE = "mix.duplicate"
        const val OP_DELETE = "mix.delete"
        const val OP_SHARE = "mix.share"
        const val OP_REGENERATE_PREVIEW = "mix.regeneratePreview"
        const val OP_SET_SHARE_ACTIVE = "mix.setShareActive"
        const val OP_OBSERVE_SHARED = "mix.observeShared"
    }
}

/** One snapshot of the signed-in user's mirrored collections. */
private class WMUserCollections(
    val uid: String,
    val mixes: List<WMStoredMix>,
    val uploads: List<WMUpload>,
    val shares: List<WMOwnedShare>,
) {
    companion object {
        /** What a plan without the collections mirrors: nothing. */
        fun empty(uid: String) = WMUserCollections(uid, emptyList(), emptyList(), emptyList())
    }
}

/** The only code that touches Firestore for mixes, uploads and shared mixes. Paths and field names live here only. */
internal interface WMMixFirestoreApi {
    /** The uid of the signed-in user, or `null`; re-emits on every auth state change. */
    val signedInUidFlow: Flow<String?>
    fun storedMixesFlow(uid: String): Flow<List<WMStoredMix>>
    fun uploadsFlow(uid: String): Flow<List<WMUpload>>
    /** The user's records of their shares, newest first. */
    fun ownSharesFlow(uid: String): Flow<List<WMOwnedShare>>
    suspend fun fetchMix(uid: String, mixId: String): WMStoredMix?
    /** Returns the new mix id. */
    suspend fun saveMix(uid: String, title: String, occupations: List<WMFirebaseMix.Occupation>, filters: WMMixFilters): String
    suspend fun updateMix(uid: String, mixId: String, title: String, occupations: List<WMFirebaseMix.Occupation>, filters: WMMixFilters)
    suspend fun renameMix(uid: String, mixId: String, title: String)
    suspend fun deleteMix(uid: String, mixId: String)
    /**
     * The public shared mix; a link its owner turned off fails with [WMSharedLinkDeactivatedException] (the rules
     * refuse to read it).
     */
    fun sharedMixFlow(sharedMixId: String): Flow<WMSharedMix?>
    /** Whether [uid]'s plan is Pro, as their user document says. */
    fun isProFlow(uid: String): Flow<Boolean>
    /** Whether [uid]'s plan is Basic or Pro, as their user document says: the plans the rules open the collections to. */
    fun isPaidFlow(uid: String): Flow<Boolean>
    /** The click statistics of [uid]'s share links; refused unless the plan is Pro. */
    fun shareStatsFlow(uid: String): Flow<List<WMShareStats>>
}

internal fun WMMixFirestoreApi(auth: FirebaseAuth, firestore: FirebaseFirestore): WMMixFirestoreApi = WMMixFirestoreApiImpl(auth, firestore)

private class WMMixFirestoreApiImpl(private val auth: FirebaseAuth, private val firestore: FirebaseFirestore): WMMixFirestoreApi {

    private fun userDoc(uid: String): DocumentReference = firestore.collection("users").document(uid)
    private fun storedMixes(uid: String): CollectionReference = userDoc(uid).collection("storedMixes")
    private fun uploads(uid: String): CollectionReference = userDoc(uid).collection("uploads")
    private fun ownShares(uid: String): CollectionReference = userDoc(uid).collection("sharedMixes")
    private fun sharedMix(id: String): DocumentReference = firestore.collection("public").document("s").collection("mix").document(id)

    override val signedInUidFlow: Flow<String?>
        get() = auth.authStateChanged.map { it?.uid }

    override fun storedMixesFlow(uid: String): Flow<List<WMStoredMix>> = storedMixes(uid).orderBy("createdAt").snapshots().map { snapshot ->
        snapshot.documents
            .map { doc -> doc.data<StoredMixDocument>() to doc.id }
            .filterNot { (document, _) -> document.isDeleted }
            .map { (document, id) -> document.toDomain(id) }
    }

    override fun ownSharesFlow(uid: String): Flow<List<WMOwnedShare>> {
        return ownShares(uid).orderBy("createdAt", Direction.DESCENDING).snapshots().map { snapshot ->
            snapshot.documents.map { doc -> doc.data<OwnedShareDocument>().toDomain(doc.id) }
        }
    }

    /**
     * Joins the uploads with the stored mixes' `fileId`s so uploads from before the pipeline
     * recorded `mixId`/`status` still resolve to their mix (see [UploadDocument.toDomain]).
     */
    override fun uploadsFlow(uid: String): Flow<List<WMUpload>> {
        return combine(uploads(uid).orderBy("uploadedAt").snapshots(), storedMixes(uid).snapshots()) { uploadSnapshot, mixSnapshot ->
            val mixIdsByFileId = mixSnapshot.documents.mapNotNull { doc ->
                val mix = doc.data<StoredMixDocument>()
                mix.fileId?.takeIf { !mix.isDeleted }?.let { it to doc.id }
            }.toMap()
            uploadSnapshot.documents
                .map { doc -> doc.data<UploadDocument>().toDomain(doc.id, mixIdsByFileId) }
                .filter { it.contentType.isNotEmpty() || it.originalFileName.isNotEmpty() }
                .sortedByDescending { it.uploadedAt }
        }
    }

    override suspend fun fetchMix(uid: String, mixId: String): WMStoredMix? {
        val snapshot = storedMixes(uid).document(mixId).get()
        if (!snapshot.exists) {
            return null
        }
        val document = snapshot.data<StoredMixDocument>()
        return if (document.isDeleted) null else document.toDomain(snapshot.id)
    }

    override suspend fun saveMix(uid: String, title: String, occupations: List<WMFirebaseMix.Occupation>, filters: WMMixFilters): String = planRequiredOnRefusal {
        val doc = storedMixes(uid).add(
            data = mapOf(
                "createdAt" to FieldValue.serverTimestamp,
                "modifiedAt" to FieldValue.serverTimestamp,
                "title" to title,
                "isDeleted" to false,
                "occupations" to occupations.toDocuments(),
                "filters" to filters.toDocument()
            )
        )
        doc.id
    }

    override suspend fun updateMix(uid: String, mixId: String, title: String, occupations: List<WMFirebaseMix.Occupation>, filters: WMMixFilters) = planRequiredOnRefusal {
        storedMixes(uid).document(mixId).update(
            mapOf(
                "modifiedAt" to FieldValue.serverTimestamp,
                "title" to title,
                "occupations" to occupations.toDocuments(),
                "filters" to filters.toDocument()
            )
        )
    }

    override suspend fun renameMix(uid: String, mixId: String, title: String) = planRequiredOnRefusal {
        storedMixes(uid).document(mixId).update(mapOf("modifiedAt" to FieldValue.serverTimestamp, "title" to title))
    }

    override suspend fun deleteMix(uid: String, mixId: String) = planRequiredOnRefusal {
        storedMixes(uid).document(mixId).update(mapOf("modifiedAt" to FieldValue.serverTimestamp, "isDeleted" to true))
    }

    /** Runs a write the rules allow only on a paid plan; their refusal becomes [WMPlanRequiredException]. */
    private suspend fun <T> planRequiredOnRefusal(write: suspend () -> T): T {
        return try {
            write()
        } catch (e: FirebaseFirestoreException) {
            if (e.code == FirestoreExceptionCode.PERMISSION_DENIED) {
                throw WMPlanRequiredException()
            }
            throw e
        }
    }

    override fun sharedMixFlow(sharedMixId: String): Flow<WMSharedMix?> {
        return sharedMix(sharedMixId).snapshots
            .map { snapshot -> if (snapshot.exists) snapshot.data<KxsWMSharedMix>() else null }
            .catch { error ->
                if (error is FirebaseFirestoreException && error.code == FirestoreExceptionCode.PERMISSION_DENIED) {
                    throw WMSharedLinkDeactivatedException()
                }
                throw error
            }
    }

    override fun isProFlow(uid: String): Flow<Boolean> = entitledPlanFlow(uid).map { it == WMSubscriptionType.Pro }

    override fun isPaidFlow(uid: String): Flow<Boolean> = entitledPlanFlow(uid).map { it != WMSubscriptionType.None }

    /** The plan [uid] may use now ([entitledPlanOf]): `none` without a document and once the subscription lapsed. */
    private fun entitledPlanFlow(uid: String): Flow<WMSubscriptionType> = userDoc(uid).snapshots.map { snapshot ->
        val user = snapshot.takeIf { it.exists }?.data<UserDocument>()
        val status = user?.status?.toEnumValue(WMUserStatus.Created)
        entitledPlanOf(status = status, plan = user?.subscriptionType?.toEnumValue(WMSubscriptionType.None))
    }

    override fun shareStatsFlow(uid: String): Flow<List<WMShareStats>> {
        return userDoc(uid).collection("sharedMixStats").snapshots().map { snapshot ->
            snapshot.documents.map { doc -> doc.data<ShareStatsDocument>().toDomain(doc.id) }
        }
    }

    private fun List<WMFirebaseMix.Occupation>.toDocuments(): List<Map<String, Any>> = map { occupation ->
        mapOf(
            "id" to occupation.id,
            "percentage" to occupation.percentage,
            "justification" to occupation.justification,
            "isEnabled" to occupation.isEnabled
        )
    }

    private fun WMMixFilters.toDocument(): Map<String, Any?> = mapOf("areaId" to areaId, "industryId" to industryId)
}
